// ─────────────────────────────────────────────────────────────
// JerryMarkerRenderer — renders custom lane markers on canvas.
// Mirrors OpenDAW's MarkerRenderer style exactly, including the
// filled pill highlight for the active (playhead-overlapping) marker.
// ─────────────────────────────────────────────────────────────

import {CanvasPainter, TimelineRange} from "@opendaw/studio-core"
import {Context2d} from "@opendaw/lib-dom"
import {JerryMarker} from "./JerryMarkerTypes"

const TEXT_PADDING = 8 as const

export namespace JerryMarkerRenderer {

    export const createTrackRenderer = (
        canvas: HTMLCanvasElement,
        range: TimelineRange,
        getMarkers: () => ReadonlyArray<JerryMarker>,
        getPosition: () => number,
        hue: number
    ) => new CanvasPainter(canvas, ({context}) => {
        const {width, height} = canvas
        const {fontFamily, fontSize} = getComputedStyle(canvas)
        context.clearRect(0, 0, width, height)
        context.textBaseline = "middle"
        context.font = `${parseFloat(fontSize) * devicePixelRatio}px ${fontFamily}`

        const markers = getMarkers()
        if (markers.length === 0) {return}

        const unitMin = range.unitMin
        const unitMax = range.unitMax

        // Determine the active marker: last marker whose position <= playhead
        const playhead = getPosition()
        let activeIdx = -1
        for (let i = 0; i < markers.length; i++) {
            if (markers[i].position <= playhead) {activeIdx = i}
            else {break}
        }

        // Only render markers visible in current scroll range (±buffer)
        for (let i = 0; i < markers.length; i++) {
            const curr = markers[i]
            if (curr.position > unitMax + 10000) {break}
            if (curr.position < unitMin - 10000) {continue}

            const next = markers[i + 1]
            const isActive = i === activeIdx
            renderMarker(context, range, curr, next, height, hue, isActive)
        }
    })

    export const renderMarker = (
        context: CanvasRenderingContext2D,
        range: TimelineRange,
        marker: JerryMarker,
        next: JerryMarker | undefined,
        height: number,
        hue: number,
        isActive: boolean
    ): void => {
        const x0 = Math.floor(range.unitToX(marker.position) * devicePixelRatio)
        const vPadding = Math.ceil(height / 5)

        // Truncate label to fit available space
        let text: string
        let textWidth: number
        if (next !== undefined) {
            const x1 = Math.floor(range.unitToX(next.position) * devicePixelRatio)
            const maxWidth = x1 - x0 - (isActive ? TEXT_PADDING << 2 : TEXT_PADDING)
            const truncated = Context2d.truncateText(context, marker.label, maxWidth)
            text = truncated.text
            textWidth = truncated.width
        } else {
            text = marker.label
            textWidth = context.measureText(text).width
        }

        if (isActive) {
            // Filled pill (same as OpenDAW's isPlaying style)
            // Flat left edge at tick, rounded right cap
            context.beginPath()
            context.roundRect(x0, vPadding, textWidth + TEXT_PADDING * 2, height - (vPadding << 1), [0, 99, 99, 0])
            context.fillStyle = `hsl(${hue}, 60%, 40%)`
            context.fill()
            context.fillStyle = `hsl(${hue}, 60%, 5%)`
            context.fillText(text, x0 + TEXT_PADDING, height >> 1)
        } else {
            // Vertical tick + label
            context.fillStyle = `hsl(${hue}, 60%, 40%)`
            context.fillRect(x0, vPadding, 2, height - (vPadding << 1))
            context.fillStyle = `hsl(${hue}, 60%, 75%)`
            context.fillText(text, x0 + TEXT_PADDING, height >> 1)
        }
    }

    // Hit test: returns the marker at the given x position or null
    export const hitTest = (
        context: CanvasRenderingContext2D,
        range: TimelineRange,
        markers: ReadonlyArray<JerryMarker>,
        localX: number
    ): JerryMarker | null => {
        // Find rightmost marker whose position <= click
        let found: JerryMarker | null = null
        for (const m of markers) {
            if (range.unitToX(m.position) <= localX) {
                found = m
            } else {
                break
            }
        }
        if (!found) {return null}

        // Check if click is within label width
        const x0 = range.unitToX(found.position)
        const labelWidth = context.measureText(found.label).width / devicePixelRatio + TEXT_PADDING * 2
        return localX - x0 < labelWidth ? found : null
    }
}
